/**
 * @file コンポーネント：フル動画ページヘッダー ロジック管理
 */

// ライブラリ読み込み
import React, { useEffect, useRef, useState } from "react";

// Presenter読み込み
import { Presenter } from "./presenter";

// 関数読み込み
import {
    fnGetCookie, fnSetCookie, fnGetParameter,
    fnGetCultureLanguage,
    fnHandleApiErrors,
    fnHandleApiSuccess,
    getListLanguage,
} from "~/src/functions/common";

// コンポーネントを返す
export const Header = (props) => {
    const listLanguage = getListLanguage(); // 選択言語リスト
    let languageCodeOperation = fnGetCultureLanguage(); // 選択中の言語

    /**
     * windowオブジェクトからのデータ取得
     */
    const isLogin = window.dataUser.isLogin; // ログイン状態
    const isIdentificationUser = window.dataUser.isIdentificationUser; // 特定リンクユーザーか否か
    const isOpexparkUser = window.dataUser.isOpexparkUser; // オペパークユーザーか否か

    /**
     * アカウントメニュー開閉管理
     */
    const [isOpenAccountmenu, setIsOpenAccountmenu] = useState(false);
    // アカウントメニュー範囲外をクリックしたらアカウントメニューを閉じる
    const refAccountmenu = useRef(null); // アカウントメニュー
    const refButtonAccount = useRef(null); // アカウントボタン
    const fnCloseAccountmenu = (e) => {
        if (
            !refAccountmenu.current.contains(e.target) &&
            !refButtonAccount.current.contains(e.target)
        ) {
            setIsOpenAccountmenu(false);
        }
    };
    useEffect(() => {
        if (isOpenAccountmenu === true) {
            document.addEventListener("click", fnCloseAccountmenu);
        } else {
            document.removeEventListener("click", fnCloseAccountmenu);
        }
    }, [isOpenAccountmenu]);

    /**
     * アカウントメニュー通知アイコン管理
     */
    const [isNotificationAccount, setIsNotificationAccount] = useState(false); // アカウントメニュー通知アイコン状態
    // 切り替え可能言語が2つ以上かつcookieに値が無ければ通知をONにする
    useEffect(() => {
        if (
            window.dataUser.userLanguages.length > 1 &&
            fnGetCookie("checkedLanguagemenu_v4.3") === undefined
        ) {
            setIsNotificationAccount(true);
        }
    }, []);
    // アカウントメニュー開閉状態変更タイミングで処理
    useEffect(() => {
        // アカウントメニューを開いた時、cookieに値が無ければ値をセット
        if (
            isOpenAccountmenu === true &&
            fnGetCookie("checkedLanguagemenu_v4.3") === undefined
        ) {
            fnSetCookie("checkedLanguagemenu_v4.3", "true", 3650, "/");
        }
        // アカウントメニューを閉じた時、cookieに値があれば通知をOFFにする
        if (
            !isOpenAccountmenu === true &&
            fnGetCookie("checkedLanguagemenu_v4.3") === "true"
        ) {
            setIsNotificationAccount(false);
        }
        // スポンサーユーザーの場合は強制的に通知をOFFにする
        if (props.isSponsorUser === true) {
            setIsNotificationAccount(false);
        }
    }, [isOpenAccountmenu]);

    /**
     * ログイン関連状態変数
     */
    const [email, setEmail] = useState(null); // メールアドレス
    const [password, setPassword] = useState(null); // パスワード
    const [loginErrorMessages, setLoginErrorMessages] = useState([]); // ログインエラーメッセージ
    const [redirectUrl, setRedirectUrl] = useState(location.href); // リダイレクトURL
    // リダイレクトURLの生成
    useEffect(() => {
        let result = "";
        let valueRefPage = fnGetParameter("refPage");
        let promotionId = fnGetParameter("promotionId");
        if (valueRefPage) {
            // refPageパラメーターがあればそのページに遷移させる
            const urlPattern = /^https?:\/\/[\w/:%#$&?()~.=+-]+$/;
            if (!urlPattern.test(valueRefPage)) {
                result += location.origin;
                valueRefPage = decodeURIComponent(valueRefPage); // URLエンコードされている場合があるのでデコード
                result += valueRefPage.charAt(0) !== "/" ? "/" : ""; // 冒頭が「/」出ない場合は「/」を付与
            }
            result += valueRefPage;
            if (promotionId) {
                result += "/?promotionId=" + promotionId;
            }
        } else {
            // refPageパラメーターがなければ現在のページに遷移させる
            result += location.href;
        }
        setRedirectUrl(result);
    }, [location.href]);

    /**
     * モーダル、サイドメニュー開閉管理
     */
    const [isOpenModalLogout, setIsOpenModalLogout] = useState(false); // ログアウトモーダル開閉状態
    const [isOpenModalNewPassword, setIsOpenModalNewPassword] = useState(false); // パスワード変更モーダル
    const [isOpenSidemenu, setIsOpenSidemenu] = useState(false); // サイドメニュー

    /**
     * 国内アクセス判定
     */
    const [isJpAccess, setIsJpAccess] = useState(true);
    useEffect(() => {
        const api = "/api/clients/jpFlag";
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    setIsJpAccess(result.jpFlag);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    /**
     * 診療科リスト取得
     */
    const [departmentlist, setDepartmentlist] = useState(null);
    useEffect(() => {
        const api =
            "/api/routes/departments?languageCode=" + fnGetCultureLanguage();
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    setDepartmentlist(result);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, [isJpAccess]);

    // Presenterに渡す
    return (
        <Presenter
            {...props}
            departmentlist={departmentlist}
            email={email}
            isLogin={isLogin}
            isNotificationAccount={isNotificationAccount}
            isOpenAccountmenu={isOpenAccountmenu}
            isOpenModalLogout={isOpenModalLogout}
            isOpenModalNewPassword={isOpenModalNewPassword}
            isOpenSidemenu={isOpenSidemenu}
            isIdentificationUser={isIdentificationUser}
            languageCodeOperation={languageCodeOperation}
            listLanguage={listLanguage}
            loginErrorMessages={loginErrorMessages}
            password={password}
            redirectUrl={redirectUrl}
            refAccountmenu={refAccountmenu}
            refButtonAccount={refButtonAccount}
            setEmail={setEmail}
            setIsOpenAccountmenu={setIsOpenAccountmenu}
            setIsOpenModalLogout={setIsOpenModalLogout}
            setIsOpenModalNewPassword={setIsOpenModalNewPassword}
            setIsOpenSidemenu={setIsOpenSidemenu}
            setLoginErrorMessages={setLoginErrorMessages}
            setPassword={setPassword}
        />
    );
};
